import type { StateCreator } from 'zustand';
import type { AppState } from '../types';
import { updateActiveProfile } from '../helpers';
import { cloneTriathlonProgression, defaultProgression, defaultCyclingProgression, defaultPlanSettings } from '../constants';
import type { PlanState, WeekSchedule, TrainingBlock, PlanConfig, ScheduledWorkout, TriathlonProgression, Profile } from '@/types';
import { recalculateWeekSummary } from '../../scheduler/utils';
import { mergeTriathlonProgression } from '@/lib/triathlon/weekly-rhythm';

export interface PlanSlice {
    plan: WeekSchedule[];
    planState: PlanState;
    planName?: string;
    planHistory: AppState['planHistory'];
    blocks: TrainingBlock[];
    config: PlanConfig | null;
    modifications: Record<string, unknown>;
    progression: { startingVolume: number; progressionRate: number };
    cyclingProgression: AppState['cyclingProgression'];
    planSettings: AppState['planSettings'];
    triathlonProgression: AppState['triathlonProgression'];

    setPlan: (weeks: WeekSchedule[]) => void;
    setBlocks: (blocks: TrainingBlock[]) => void;
    setPlanConfig: (config: PlanConfig | null) => void;
    updateWorkout: (weekNum: number, day: number, slot: 'am' | 'pm', workout: ScheduledWorkout | null) => void;
    updateWorkoutStatus: (weekNum: number, day: number, slot: 'am' | 'pm', status: ScheduledWorkout['completionStatus']) => void;
    setProgression: (prog: Partial<AppState['progression']>) => void;
    setCyclingProgression: (prog: Partial<AppState['cyclingProgression']>) => void;
    setValidationWarnings: (warnings: AppState['validationWarnings']) => void;
    updatePlanSettings: (settings: Partial<AppState['planSettings']>) => void;
    setTriathlonProgression: (prog: Partial<TriathlonProgression>) => void;
    setPlanState: (state: PlanState) => void;
    setPlanName: (name: string) => void;
    toggleWeekLock: (weekNum: number) => void;
    archiveCurrentPlan: (name?: string) => void;
    activatePlan: () => void;
    restorePlan: (planId: string) => void;
    deletePlanFromHistory: (planId: string) => void;
}

export const createPlanSlice: StateCreator<AppState, [], [], PlanSlice> = (set) => ({
    plan: [],
    planState: 'active' as PlanState,
    planName: undefined,
    planHistory: [],
    blocks: [],
    config: null,
    modifications: {},
    progression: { ...defaultProgression },
    cyclingProgression: { ...defaultCyclingProgression },
    planSettings: { ...defaultPlanSettings },
    triathlonProgression: cloneTriathlonProgression(),

    setPlan: (weeks) => set((state) => ({
        plan: weeks,
        ...updateActiveProfile(state, { plan: weeks }),
    })),

    setBlocks: (blocks) => set((state) => ({
        blocks,
        ...updateActiveProfile(state, { blocks }),
    })),

    setPlanConfig: (config) => set((state) => ({
        config,
        ...updateActiveProfile(state, { config }),
    })),

    updateWorkout: (weekNum, day, slot, workout) => set((state) => {
        const newPlan = [...state.plan];
        const weekIndex = newPlan.findIndex(w => w.weekNumber === weekNum);
        if (weekIndex !== -1) {
            const newDays = [...newPlan[weekIndex].days];
            if (slot === 'am') {
                newDays[day] = { ...newDays[day], workout };
            } else {
                newDays[day] = { ...newDays[day], secondary: workout };
            }
            // Recalculate totals for this week
            const updatedWeek = recalculateWeekSummary({ ...newPlan[weekIndex], days: newDays });
            newPlan[weekIndex] = updatedWeek;
        }
        return {
            plan: newPlan,
            ...(updateActiveProfile(state, { plan: newPlan }) as Partial<AppState>),
        };
    }),

    updateWorkoutStatus: (weekNum, day, slot, status) => set((state) => {
        const newPlan = [...state.plan];
        const weekIndex = newPlan.findIndex(w => w.weekNumber === weekNum);
        if (weekIndex !== -1) {
            const newDays = [...newPlan[weekIndex].days];
            const target = slot === 'am' ? newDays[day].workout : newDays[day].secondary;

            if (target) {
                const updatedWorkout = {
                    ...target,
                    completionStatus: status,
                    isCompleted: status === 'done',
                    completedAt: status === 'done' ? new Date().toISOString() : undefined
                };

                if (slot === 'am') {
                    newDays[day] = { ...newDays[day], workout: updatedWorkout };
                } else {
                    newDays[day] = { ...newDays[day], secondary: updatedWorkout };
                }
                newPlan[weekIndex] = { ...newPlan[weekIndex], days: newDays };
            }
        }

        // Ideally here we would also update gamification stats, 
        // but since we derive them in the HUD/Dashboard, we might not need to bake it here.
        // However, the gamification-slice could listen or we could trigger it.

        return {
            plan: newPlan,
            ...(updateActiveProfile(state, { plan: newPlan }) as Partial<AppState>),
        };
    }),

    setProgression: (prog) => set((state) => {
        const newProgression = { ...state.progression, ...prog };
        return {
            progression: newProgression,
            ...(updateActiveProfile(state, { progression: newProgression }) as Partial<AppState>),
        };
    }),

    setCyclingProgression: (prog) => set((state) => {
        const newCyclingProgression = { ...state.cyclingProgression, ...prog };
        return {
            cyclingProgression: newCyclingProgression,
            ...(updateActiveProfile(state, { cyclingProgression: newCyclingProgression }) as Partial<AppState>),
        };
    }),

    setValidationWarnings: (warnings) => set((state) => ({
        validationWarnings: warnings,
        ...updateActiveProfile(state, { validationWarnings: warnings } as Partial<Profile>),
    })),

    updatePlanSettings: (settings) => set((state) => {
        const newPlanSettings = { ...state.planSettings, ...settings };
        return {
            planSettings: newPlanSettings,
            ...(updateActiveProfile(state, { planSettings: newPlanSettings }) as Partial<AppState>),
        };
    }),

    setTriathlonProgression: (prog) => set((state) => {
        const newTriathlonProgression = mergeTriathlonProgression(state.triathlonProgression, prog);
        return {
            triathlonProgression: newTriathlonProgression,
            ...(updateActiveProfile(state, { triathlonProgression: newTriathlonProgression }) as Partial<AppState>),
        };
    }),

    setPlanState: (planState) => set((state) => ({
        planState,
        ...(updateActiveProfile(state, { planState }) as Partial<AppState>),
    })),

    setPlanName: (planName) => set((state) => ({
        planName,
        ...(updateActiveProfile(state, { planName }) as Partial<AppState>),
    })),
    toggleWeekLock: (weekNum) => set((state) => {
        const newPlan = state.plan.map(w =>
            w.weekNumber === weekNum ? { ...w, locked: !w.locked } : w
        );
        return {
            plan: newPlan,
            ...(updateActiveProfile(state, { plan: newPlan }) as Partial<AppState>),
        };
    }),

    archiveCurrentPlan: (name) => set((state) => {
        if (state.plan.length === 0) return state;

        const archivedPlan = {
            id: crypto.randomUUID(),
            name: name || state.planName || `Plan ${new Date().toLocaleDateString()}`,
            archivedAt: new Date().toISOString(),
            weeks: state.plan,
            blocks: state.blocks
        };

        const newPlanHistory = [...state.planHistory, archivedPlan];

        return {
            planHistory: newPlanHistory,
            plan: [],
            blocks: [],
            planState: 'draft' as PlanState,
            planName: undefined,
            ...(updateActiveProfile(state, {
                planHistory: newPlanHistory,
                plan: [],
                blocks: [],
                planState: 'draft' as PlanState,
                planName: undefined,
            }) as Partial<AppState>),
        };
    }),

    activatePlan: () => set((state) => ({
        planState: 'active' as PlanState,
        ...(updateActiveProfile(state, { planState: 'active' as PlanState }) as Partial<AppState>),
    })),

    restorePlan: (planId) => set((state) => {
        const archived = state.planHistory.find(p => p.id === planId);
        if (!archived) return state;

        let newHistory = state.planHistory.filter(p => p.id !== planId);
        if (state.plan.length > 0) {
            newHistory = [...newHistory, {
                id: crypto.randomUUID(),
                name: state.planName || `Plan ${new Date().toLocaleDateString()}`,
                archivedAt: new Date().toISOString(),
                weeks: state.plan,
                blocks: state.blocks
            }];
        }

        return {
            plan: archived.weeks,
            blocks: archived.blocks,
            planName: archived.name,
            planState: 'active' as PlanState,
            planHistory: newHistory,
            ...(updateActiveProfile(state, {
                plan: archived.weeks,
                blocks: archived.blocks,
                planName: archived.name,
                planState: 'active' as PlanState,
                planHistory: newHistory,
            }) as Partial<AppState>),
        };
    }),

    deletePlanFromHistory: (planId) => set((state) => {
        const newPlanHistory = state.planHistory.filter(p => p.id !== planId);
        return {
            planHistory: newPlanHistory,
            ...(updateActiveProfile(state, { planHistory: newPlanHistory }) as Partial<AppState>),
        };
    }),
});
